// External-link interception for the native mobile shells (Caveat #19).
//
// A plain <a href="https://…"> inside a Tauri WebView navigates THE WEBVIEW
// ITSELF. There is no browser chrome around it, so the app shell is simply
// replaced by a web page: the socket drops, in-memory state and unsent drafts
// go with it, and the user is left browsing the web with no URL bar to tell
// them where they are.
//
// This lived inline in +layout.svelte inside an `isTauriIOS()` block, and its
// comment justified it with App Review — an iOS-only consequence of a
// NOT-iOS-only cause. Android runs the same WebView navigation and loses the
// same shell; it merely has a back button to crawl out with, which returns to a
// COLD app, not to where the user was. Same class as #2306's deep-link drains,
// which were gated the same way for the same reason.
//
// Extracted rather than copied so the two shells can't drift apart again.
import { isTauriAndroid, isTauriIOS } from "./platform.js";

/** True in either native mobile shell — where a WebView navigation eats the app. */
export function externalLinkInterceptionSupported(): boolean {
  return isTauriIOS() || isTauriAndroid();
}

/** Schemes that must leave the app instead of navigating the WebView. */
const EXTERNAL_SCHEME = /^(https?:|mailto:|tel:)/i;

/**
 * Route external anchors through the OS browser/handler. Returns the teardown.
 *
 * Capture phase: SvelteKit installs its own link interceptor, and this has to
 * win before it. A no-op (returning a no-op teardown) off the mobile shells.
 */
export function installExternalLinkInterception(): () => void {
  if (!externalLinkInterceptionSupported()) return () => {};
  if (typeof document === "undefined") return () => {};

  const onLinkClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    const anchor = target?.closest("a") as HTMLAnchorElement | null;
    // A link inside an editor is text being edited: the click places the caret.
    if (!anchor || anchor.isContentEditable) return;
    const href = anchor.getAttribute("href") ?? "";
    if (!EXTERNAL_SCHEME.test(href)) return;
    e.preventDefault();
    e.stopPropagation();
    void (async () => {
      try {
        const { openUrl } = await import("@tauri-apps/plugin-opener");
        await openUrl(href);
      } catch (err) {
        // The plugin is the only way OUT of the WebView, so a failure here is
        // the case this module exists to prevent. window.open still beats a
        // same-frame navigation: it cannot replace the app shell.
        console.warn("[external-links] opener failed, falling back to window.open", err);
        window.open(href, "_blank", "noopener,noreferrer");
      }
    })();
  };

  document.addEventListener("click", onLinkClick, { capture: true });
  return () => document.removeEventListener("click", onLinkClick, { capture: true });
}
